import React, { useRef, useState } from 'react';
import { Loader2, Bot } from 'lucide-react';
import { config } from '../config';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { setIsSalespersonHost, clearConversation } from '../store/conversationSlice';
import { clearLandmineAlerts } from '../store/slices/landmineSlice';
import { clearCompetitorAlerts } from '../store/slices/competitorSlice';
import { store } from '../store';
import { CalendarBot } from '../services/calendarWebSocketService';
import { generateClientId } from '../utils/clientId';
import { EndOfCallRequest } from '../hooks/useEndOfCallFlow';
import { terminateManualBot } from '../services/botApi';
import { BOT_API_COPY, BOT_CLIENT_PLATFORM, BOT_REMOVAL_OUTCOME, BotRemovalOutcome } from '../constants/botApi';
import { describeBotApiError, isBotGoneError, removalOutcomeOf } from '../utils/botApiErrors';
import { useToast } from '../contexts/ToastContext';
import { Button } from './ui/button';
import { ConfirmModal } from './ui/ConfirmModal';
import { AlertModal } from './ui/AlertModal';
import { BOT_ADMISSION_TIMEOUT_MS, BotNotAdmittedReason } from '../constants/botAdmission';
import { waitForBotAdmission } from '../services/botAdmissionWait';
import { disconnectLiveCallSockets } from '../services/liveCallSockets';

interface MeetBotModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBotCreated: (sessionId: string, websocketUrl: string) => void | Promise<void>;
  isBotConnected?: boolean; // Track if bot is connected from parent
  onBotConnectionStateChange?: (isConnected: boolean) => void; // Callback to update parent state
  botType?: 'manual' | 'calendar' | null; // Type of connected bot
  botSessionId?: string | null; // Session ID of connected bot (for API calls)
  botDisplayId?: string | null; // Bot ID to display in UI
  setCalendarBots?: React.Dispatch<React.SetStateAction<CalendarBot[]>>; // For adding manual bots to calendar bots state
  defaultBotName?: string; // Default bot name from calendar settings
  isSessionCreator?: boolean; // Only the creator reviews participants after the call
  /** Runs the page's end-of-call flow (participant review, then the summary). */
  onSessionEnded?: (request: EndOfCallRequest) => Promise<void> | void;
  /** The bot this modal created was never let into the meeting: the page shows why. */
  onBotNotAdmitted?: (reason: BotNotAdmittedReason) => void;
}

export const MeetBotModal: React.FC<MeetBotModalProps> = ({
  isOpen,
  onClose,
  onBotCreated,
  isBotConnected: parentBotConnected = false,
  onBotConnectionStateChange,
  botType = null,
  botSessionId = null,
  botDisplayId = null,
  setCalendarBots,
  defaultBotName,
  isSessionCreator = false,
  onSessionEnded,
  onBotNotAdmitted,
}) => {

  const dispatch = useAppDispatch();
  const { showToast } = useToast();

  // Get transcripts for checking if we need to clear previous session
  const transcripts = useAppSelector((state) => state.conversation.transcripts);

  const [meetingUrl, setMeetingUrl] = useState('');
  const [eventTitle, setEventTitle] = useState('');  // Session/meeting name (mandatory)
  const [botName, setBotName] = useState(defaultBotName || 'Transcriber');
  const [isLoading, setIsLoading] = useState(false);

  // Update bot name when defaultBotName changes (after calendar settings load)
  React.useEffect(() => {
    if (defaultBotName && botName === 'Transcriber') {
      setBotName(defaultBotName);
    }
    // botName is read only as a guard so the default is applied once when it loads; adding it
    // would overwrite what the user typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultBotName]);
  const [loadingMessage, setLoadingMessage] = useState('Creating Bot...');
  const [error, setError] = useState<string | null>(null);
  const [detectedPlatform, setDetectedPlatform] = useState<'meet' | 'zoom' | 'teams' | null>(null);
  const [isBotConnected, setIsBotConnected] = useState(false);
  const [connectedBotId, setConnectedBotId] = useState<string | null>(null);
  // The lobby: the bot exists and waits for the host to let it in. Cancel is
  // live only here (it removes the bot), as in the desktop apps' lobby.
  const [isAwaitingAdmission, setIsAwaitingAdmission] = useState(false);
  const pendingBotIdRef = useRef<string | null>(null);
  const cancelAdmissionWaitRef = useRef<(() => void) | null>(null);

  // Sync modal state with parent's bot connection state
  React.useEffect(() => {
    if (isOpen) {
      // When modal opens or parent state changes, sync with parent's bot connection state
      setIsBotConnected(parentBotConnected);

      // If bot was terminated externally (parentBotConnected became false), clear internal state
      if (!parentBotConnected) {
        setConnectedBotId(null);
        setDetectedPlatform(null);
        console.log('🔄 Modal synced with parent: bot disconnected');
      }
    }
  }, [isOpen, parentBotConnected, botType, botSessionId, botDisplayId]);

  const detectPlatform = (url: string): 'meet' | 'zoom' | 'teams' | null => {
    const meetPattern = /^https:\/\/meet\.google\.com\/[a-z]{3}-[a-z]{4}-[a-z]{3}$/;
    const zoomPatterns = [
      /^https?:\/\/[\w.-]*zoom\.us\/j\/(\d{9,11})(\?pwd=[\w.-]+)?$/,
      /^https?:\/\/[\w.-]*zoom\.us\/my\/([\w.-]+)(\?pwd=[\w.-]+)?$/,
      /^https?:\/\/[\w.-]*zoom\.us\/w\/(\d{9,11})(\?pwd=[\w.-]+)?$/,
      /^https?:\/\/[\w.-]*zoomgov\.com\/j\/(\d{9,11})(\?pwd=[\w.-]+)?$/
    ];
    const teamsPattern = /^https:\/\/teams\.microsoft\.com\/l\/meetup-join\//;

    if (meetPattern.test(url)) {
      return 'meet';
    }

    for (const pattern of zoomPatterns) {
      if (pattern.test(url)) {
        return 'zoom';
      }
    }

    if (teamsPattern.test(url)) {
      return 'teams';
    }

    return null;
  };

  // Both actions ask first, in the app's own dialog. The join or the removal runs only on Yes.
  const [pendingJoin, setPendingJoin] = useState<'meet' | 'zoom' | 'teams' | null>(null);
  const [pendingRemovalSessionId, setPendingRemovalSessionId] = useState<string | null>(null);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);

  const handleCreateBot = () => {
    if (!meetingUrl.trim()) {
      setError('Please enter a valid meeting URL');
      return;
    }

    if (!eventTitle.trim()) {
      setError('Please enter a session name');
      return;
    }

    // Auto-detect platform from URL
    const platform = detectPlatform(meetingUrl);

    if (!platform) {
      setError('Please enter a valid Google Meet, Zoom, or Microsoft Teams URL');
      return;
    }

    // Store detected platform for display
    setDetectedPlatform(platform);

    setPendingJoin(platform);
  };

  const createBot = async (platform: 'meet' | 'zoom' | 'teams') => {
    // Clear previous session data if exists
    // Note: All session data is automatically saved by saver_service in real-time
    // No need to prompt user for manual save
    if (transcripts.length > 0) {
      const { sessionManager } = await import('../services/sessionManager');

      console.log('🧹 Clearing previous session data before creating new bot (auto-saved by saver_service)');

      // Clear all Redux state
      dispatch(clearConversation());
      dispatch(clearLandmineAlerts());
      dispatch(clearCompetitorAlerts());
      sessionManager.resetCallTiming();

      console.log('✅ Previous session data cleared');
    }

    setIsLoading(true);
    setLoadingMessage('Creating Bot...');
    setError(null);

    try {
      // Get Firebase auth token and user info
      const state = store.getState();
      const firebaseUid = state.auth.user?.uid;

      if (!firebaseUid) {
        throw new Error('User not authenticated');
      }

      // Get Firebase ID token for authentication
      const { auth } = await import('../config/firebase');
      const currentUser = auth.currentUser;

      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const idToken = await currentUser.getIdToken();

      // Generate client ID for this session
      const clientId = generateClientId();
      console.log('📱 Generated clientId for bot creation:', clientId);

      // Use backend server bot endpoints (integrated bot service)
      const backendUrl = config.server.backendUrl || 'http://localhost:5000';

      const response = await fetch(`${backendUrl}/api/bots/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          meetingUrl,
          platform,
          eventTitle,
          firebaseUid,
          clientId,
          clientPlatform: BOT_CLIENT_PLATFORM,
          options: {
            botName,
          },
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create bot');
      }

      // Store bot_id in localStorage and sessionManager so all services can use it
      const botId = data.botId || data.sessionId; // Some services return botId, others return sessionId
      const websocketUrl = data.websocketUrl;
      // Host default (same as the desktop apps): the participant roster
      // confirmation is the role authority now, not a create-time choice.
      dispatch(setIsSalespersonHost(true));

      // Add manual bot to calendarBots state for lifecycle tracking
      // This allows calendar_sync bot_status_poll_loop to monitor termination
      if (setCalendarBots) {
        const manualBot: CalendarBot = {
          source: 'manual',
          botSessionId: `manual_${botId}`,
          botId: botId,
          eventTitle: eventTitle,
          meetingUrl: meetingUrl,
          startTime: new Date().toISOString(),
          status: 'joining',
          platform: platform,
          websocketUrl: websocketUrl,
          isSalespersonHost: true,
        };

        // Note: Don't add to calendarBots manually - calendar_sync_service will pick it up
        // and send it via WebSocket after backend creates scheduledbots document
        console.log('✅ Manual bot will be added to calendarBots by calendar_sync_service:', manualBot);
      }

      // Note: scheduledbots document is now created during /api/bots/create call
      // No separate registration needed - backend creates the document immediately
      console.log('✅ Manual bot registered in MongoDB during creation (no separate registration needed)');

      // Update loading message for reconnection phase
      setLoadingMessage('Connecting to WebSocket...');

      // Call onBotCreated with both botId and websocketUrl
      try {
        const result = onBotCreated(botId, websocketUrl);
        if (result && typeof result === 'object' && 'then' in result) {
          await result;
        }

        // Update loading message to wait for bot to become active
        setLoadingMessage('Waiting for bot to be accepted into the meeting...');

        // Wait for the host to let the bot in (calendar bot_active, or the recall
        // socket's participant_join), or not (bot_terminated with notAdmitted:
        // Recall gave up after its 10-minute waiting room, or the host denied
        // it), or the rep's Cancel. The timeout is only a safety net past
        // Recall's limit.
        pendingBotIdRef.current = botId;
        setIsAwaitingAdmission(true);
        const admission = await waitForBotAdmission(botId, BOT_ADMISSION_TIMEOUT_MS, (cancel) => {
          cancelAdmissionWaitRef.current = cancel;
        }).finally(() => {
          pendingBotIdRef.current = null;
          cancelAdmissionWaitRef.current = null;
          setIsAwaitingAdmission(false);
        });
        if (admission.kind === 'notAdmitted') {
          // No call happened: close the sockets this bot opened (the page may
          // have forgotten the bot already), and the page closes the lobby and
          // shows why. The form keeps the meeting URL so "Add Bot to Meeting"
          // can be pressed again.
          console.log(`🚪 Bot ${botId} was never let into the meeting: ${admission.reason}`);
          disconnectLiveCallSockets();
          onBotNotAdmitted?.(admission.reason);
          return;
        }
        if (admission.kind === 'cancelled') return;

        console.log('✅ Bot has been accepted and is now active in the meeting!');

        // Mark bot as connected. Remove Bot names it by its Recall bot id, as
        // both desktop apps do: the create response's sessionId lives only in
        // the memory of the backend container that created the bot.
        setIsBotConnected(true);
        setConnectedBotId(botId);
        setMeetingUrl('');
        setError(null);

        // Notify parent component of bot connection state change
        if (onBotConnectionStateChange) {
          onBotConnectionStateChange(true);
        }

        // Auto-close modal after successful connection
        console.log('🔒 Auto-closing modal after successful bot connection');
        onClose();
      } catch (reconnectError: any) {
        console.error('❌ Error during service reconnection or bot join:', reconnectError);

        // Note: Event listeners are cleaned up in the promise handlers

        // Show appropriate error message
        if (reconnectError.message?.includes('timeout')) {
          setError('Bot created but took too long to join the meeting. Please check the meeting URL and try again.');
        } else {
          setError('Bot created but failed to connect properly. Please try again.');
        }
        setIsLoading(false);
        return;
      }

    } catch (err: any) {
      setError(err.message || 'Failed to create bot');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Cancel in the lobby: the bot exists but the host has not let it in. Stop
   * waiting, close every live socket, forget the pending bot and remove it from
   * the meeting (best effort and logged, as on the desktop apps: the rep has
   * moved on). No summary: there was no call.
   */
  const handleCancelPendingBot = async () => {
    const botId = pendingBotIdRef.current;
    cancelAdmissionWaitRef.current?.();
    disconnectLiveCallSockets();
    onBotConnectionStateChange?.(false);
    onClose();
    if (!botId) return;
    try {
      const removal = await terminateManualBot(botId);
      console.log(`✅ Pending bot ${botId} removed: ${removal}`);
    } catch (removeError) {
      console.error(`❌ Could not remove the pending bot ${botId}:`, removeError);
    }
  };

  /**
   * Remove Bot through the bot's own route: a calendar bot by its botSessionId,
   * a manual bot by its Recall bot id. The backend decides whether the bot was
   * still in the call; a 404 means it no longer knows the bot, so it has left.
   * Rejects for anything else, keeping the status so a 401/403 reads as
   * "sign in again" / "not allowed".
   */
  const requestBotRemoval = async (idToRemove: string): Promise<BotRemovalOutcome> => {
    if (botType !== 'calendar') {
      return terminateManualBot(idToRemove);
    }
    const { calendarWebSocketService } = await import('../services/calendarWebSocketService');
    const result = await calendarWebSocketService.terminateBot(idToRemove);
    if (result.success) return removalOutcomeOf(result);
    if (isBotGoneError(result)) return BOT_REMOVAL_OUTCOME.alreadyEnded;
    throw Object.assign(new Error(result.error || 'Failed to remove calendar bot'), { status: result.status });
  };

  const handleRemoveBot = () => {
    // For calendar bots: use parent props (botType, botSessionId)
    // For manual bots: use local state (connectedBotId)
    const isCalendarBot = botType === 'calendar';
    const sessionIdToRemove = isCalendarBot ? botSessionId : connectedBotId;


    if (!sessionIdToRemove) {
      console.error('❌ No session ID to remove! Cannot proceed.');
      setAlertMessage('Error: No bot session ID found. Please try reconnecting the bot.');
      return;
    }

    setPendingRemovalSessionId(sessionIdToRemove);
  };

  const removeBot = async (sessionIdToRemove: string) => {

    // Captured before teardown clears the ids and the parent's creator flag: the
    // conversation session id of a bot session is the bot id (calendar bots:
    // botDisplayId; manual: ours).
    const endedSessionId = botDisplayId || connectedBotId;
    const wasSessionCreator = isSessionCreator;

    setIsLoading(true);
    setLoadingMessage('Removing bot...');

    try {
      const removal = await requestBotRemoval(sessionIdToRemove);
      console.log(`✅ Bot removal: ${removal}`);
      // Not an error: the backend found the bot already out of the call. The
      // session still ends exactly as after a removal.
      if (removal === BOT_REMOVAL_OUTCOME.alreadyEnded) {
        showToast(BOT_API_COPY.botAlreadyLeft, 'info');
      }
      // The backend marks the bot's row terminated; calendar_sync's sweep then
      // finalizes the session and tells every app in the room.

      // Set call end time before disconnecting
      const { sessionManager } = await import('../services/sessionManager');
      sessionManager.setCallEndTime();

      // Disconnect from ALL WebSocket services
      console.log('🔌 Disconnecting from all WebSocket services...');

      const { recallWebSocketService } = await import('../services/recallWebSocketService');
      const { websocketService } = await import('../services/websocketService');
      const { landmineWebSocketService } = await import('../services/landmineWebSocketService');
      const { competitorWebSocketService } = await import('../services/competitorWebSocketService');

      recallWebSocketService.disconnect();
      websocketService.disconnect();
      landmineWebSocketService.disconnect();
      competitorWebSocketService.disconnect();

      console.log('✅ All WebSocket services disconnected');

      // Clear connection state
      setIsBotConnected(false);
      setConnectedBotId(null);
      setDetectedPlatform(null); // Clear detected platform
      setMeetingUrl(''); // Clear meeting URL

      // Reset isSalespersonHost to default in Redux store
      dispatch(setIsSalespersonHost(true));

      // CRITICAL: Notify parent component IMMEDIATELY that bot is disconnected
      // This updates the "Remove Bot" button to "Connect Bot"
      if (onBotConnectionStateChange) {
        console.log('🔴 Immediately updating parent: isBotConnected = false');
        onBotConnectionStateChange(false);
      }

      // The page's end-of-call flow: the creator's participant review, then the
      // summary. Runs once per session even if the bot's own termination event
      // also reports this end.
      await onSessionEnded?.({
        sessionId: endedSessionId,
        sessionName: eventTitle || 'Session',
        isCreator: wasSessionCreator,
      });

      // Note: Session is automatically saved by saver_service when bot disconnects
      // No manual save prompt needed - saver_service handles finalization automatically
      console.log('💾 Session automatically saved by saver_service');
      console.log('✅ Bot disconnected successfully');
    } catch (error: any) {
      console.error('❌ Error removing bot:', error);
      setError(describeBotApiError(error, BOT_API_COPY.removeForbidden, BOT_API_COPY.removeFailed));
    } finally {
      setIsLoading(false);
    }
  };


  if (!isOpen) return null;

  return (
    <div className="modal-backdrop">
      <div className="modal-panel max-w-[80vw] max-h-[calc(100vh-80px)] overflow-y-auto">
        <div className="p-6 space-y-4 min-w-[600px]">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h2 className="heading-3 flex items-center gap-2">
              <Bot className="w-6 h-6" />
              Add Meeting Bot to Call
            </h2>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${isBotConnected ? 'bg-success animate-pulse' : 'bg-ink-muted'}`} />
                <span className="text-sm text-ink-secondary">
                  {isBotConnected ? 'Bot Active' : 'Not Connected'}
                </span>
              </div>
              <Button
                variant="tertiary"
                onClick={onClose}
                disabled={isLoading}
                className="text-2xl font-bold"
              >
                ×
              </Button>
            </div>
          </div>

          <div className="space-y-6">
            {/* Show connected state if bot is connected */}
            {(() => {
              const displayBotType = botType === 'calendar' ? 'Calendar Bot' : botType === 'manual' ? 'Manual Bot' : 'Bot';
              const displayBotId = (botType === 'calendar' ? botDisplayId : connectedBotId) || 'Connecting...';


              return isBotConnected ? (
                <div className="space-y-4">
                  <div className="bg-success-tint border border-success rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-3 h-3 bg-success rounded-full animate-pulse" />
                        <div>
                          <h4 className="font-semibold text-ink">Bot Connected</h4>
                          <p className="text-sm text-ink-secondary">
                            Bot is actively monitoring the meeting
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-info-tint border border-info p-4 rounded-lg">
                    <h4 className="font-medium text-sm mb-2 text-ink">Connection Details:</h4>
                    <ul className="text-sm text-ink-secondary space-y-1">
                      <li>• Bot Type: {displayBotType}</li>
                      <li>• Bot ID: {displayBotId}</li>
                      <li>• Platform: {detectedPlatform === 'meet' ? 'Google Meet' : detectedPlatform === 'zoom' ? 'Zoom' : detectedPlatform === 'teams' ? 'Microsoft Teams' : 'Auto-detected'}</li>
                      <li>• Transcripts are being received in real-time</li>
                    </ul>
                  </div>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                  <label htmlFor="meeting-url" className="block text-sm font-medium text-ink-secondary">
                    Meeting URL
                  </label>
                  <input
                    id="meeting-url"
                    type="url"
                    placeholder="Enter Google Meet, Zoom, or Teams URL"
                    value={meetingUrl}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setMeetingUrl(e.target.value);
                      // Auto-detect platform when URL changes
                      if (e.target.value) {
                        const detected = detectPlatform(e.target.value);
                        if (detected) {
                          setDetectedPlatform(detected);
                          console.log(`Detected platform: ${detected}`);
                        }
                      }
                    }}
                    disabled={isLoading}
                    className="w-full px-4 py-2 border border-ink-border rounded-md focus:ring-2 focus:ring-signature focus:border-transparent disabled:bg-surface-subtle disabled:cursor-not-allowed"
                  />
                  {detectedPlatform && (
                    <div className="flex items-center gap-2 text-sm text-success">
                      <div className="w-2 h-2 bg-success rounded-full"></div>
                      <span>
                        Detected: {detectedPlatform === 'meet' ? 'Google Meet' : detectedPlatform === 'zoom' ? 'Zoom' : 'Microsoft Teams'}
                      </span>
                    </div>
                  )}
                  <p className="text-xs text-ink-muted">
                    Enter any meeting URL - we'll automatically detect the platform
                  </p>
                </div>

                <div className="space-y-2">
                  <label htmlFor="event-title" className="block text-sm font-medium text-ink-secondary">
                    Session Name <span className="text-error">*</span>
                  </label>
                  <input
                    id="event-title"
                    type="text"
                    placeholder="Enter a name for this session (e.g., Client Demo, Sales Call)"
                    value={eventTitle}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEventTitle(e.target.value)}
                    disabled={isLoading}
                    className="w-full px-4 py-2 border border-ink-border rounded-md focus:ring-2 focus:ring-signature focus:border-transparent disabled:bg-surface-subtle disabled:cursor-not-allowed"
                  />
                  <p className="text-xs text-ink-muted">
                    Required: This name will be used to identify this session in your records
                  </p>
                </div>

                <div className="space-y-2">
                  <label htmlFor="bot-name" className="block text-sm font-medium text-ink-secondary">
                    Bot Display Name
                  </label>
                  <input
                    id="bot-name"
                    type="text"
                    placeholder="Meeting Assistant"
                    value={botName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBotName(e.target.value)}
                    disabled={isLoading}
                    className="w-full px-4 py-2 border border-ink-border rounded-md focus:ring-2 focus:ring-signature focus:border-transparent disabled:bg-surface-subtle disabled:cursor-not-allowed"
                  />
                  <p className="text-xs text-ink-muted">
                    This name will be shown to participants in the meeting
                  </p>
                  <p className="text-xs text-ink-muted">
                    Suggested names: Transcriber, Note Taker, Meeting Assistant, Scribe
                  </p>
                </div>

                </>
              );
            })()}

            {error && (
              <div className="bg-error-tint border border-error rounded-lg p-4">
                <p className="text-sm text-ink">{error}</p>
              </div>
            )}

            <div className="bg-info-tint border border-info p-4 rounded-lg">
              <h4 className="font-medium text-sm mb-2 text-ink">How it works:</h4>
              <ol className="text-sm text-ink-secondary space-y-1">
                <li>1. The bot will join your meeting automatically</li>
                <li>2. It will capture and transcribe audio in real-time</li>
                <li>3. Live analysis will appear in your dashboard</li>
                <li>4. The bot stays muted and camera-off by default</li>
                {detectedPlatform === 'zoom' && (
                  <li className="text-xs text-ink-secondary mt-1">Note: Host may need to admit the bot from waiting room</li>
                )}
              </ol>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-hairline">
            {isBotConnected ? (
              <>
                <Button variant="secondary" onClick={onClose} disabled={isLoading}>
                  Close
                </Button>
                <Button variant="destructive" onClick={handleRemoveBot} disabled={isLoading} className="flex items-center">
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {loadingMessage}
                    </>
                  ) : (
                    'Remove Bot'
                  )}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="secondary"
                  onClick={isAwaitingAdmission ? handleCancelPendingBot : onClose}
                  disabled={isLoading && !isAwaitingAdmission}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleCreateBot}
                  disabled={isLoading || !meetingUrl.trim()}
                  className="flex items-center"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {loadingMessage}
                    </>
                  ) : (
                    'Add Bot to Meeting'
                  )}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
      <AlertModal isOpen={alertMessage !== null} message={alertMessage ?? ''} onClose={() => setAlertMessage(null)} />
      <ConfirmModal
        isOpen={pendingJoin !== null}
        title={`Join ${pendingJoin === 'meet' ? 'Google Meet' : pendingJoin === 'zoom' ? 'Zoom' : 'Microsoft Teams'} meeting?`}
        message="The bot will join the meeting automatically and start transcribing."
        confirmLabel="Join meeting"
        onConfirm={() => {
          const platform = pendingJoin;
          setPendingJoin(null);
          if (platform) void createBot(platform);
        }}
        onCancel={() => setPendingJoin(null)}
      />
      <ConfirmModal
        isOpen={pendingRemovalSessionId !== null}
        title="Remove bot from meeting?"
        message="The bot will leave the meeting, and the session will end."
        confirmLabel="Remove bot"
        destructive
        onConfirm={() => {
          const sessionId = pendingRemovalSessionId;
          setPendingRemovalSessionId(null);
          if (sessionId) void removeBot(sessionId);
        }}
        onCancel={() => setPendingRemovalSessionId(null)}
      />
    </div>
  );
};